import {getCurrentUserLogin, isLoggedIn} from '@github-ui/client-env'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {preheatIssueViewerPrimary, type IssueCoordinates} from '@github-ui/preheat-issues-manager/issue-viewer-query'
import {useAnalytics} from '@github-ui/use-analytics'
import {useEffect} from 'react'

import {preheatIssueViewerSecondary} from '../utils/issue-viewer-secondary-preheat'
import {runPreheatTasksWhenIdle} from '../utils/run-preheat-tasks-when-idle'

export function usePreheatIssueRows({
  issues,
  login,
  source,
}: {
  issues: readonly IssueCoordinates[]
  login: string
  source: string
}) {
  const {sendAnalyticsEvent} = useAnalytics({optional: true})

  useEffect(() => {
    const controller = new AbortController()
    const {signal} = controller
    const canPreheat = () =>
      !signal.aborted &&
      isLoggedIn() &&
      getCurrentUserLogin() === login &&
      isFeatureEnabled('issue_viewer_paved_path') &&
      !isFeatureEnabled('disable_preheating_for_issues')

    if (!canPreheat()) return
    const onError = () => {
      if (canPreheat())
        sendAnalyticsEvent('offline_cache.preheat_error', undefined, {mode: 'tanstack', operation: 'task'})
    }
    const whenAllowed = (preheat: () => Promise<void>) => async () => {
      if (canPreheat()) await preheat()
    }

    // Primaries run first, like the legacy high-priority tasks.
    runPreheatTasksWhenIdle(
      [
        ...issues.map(issue => whenAllowed(() => preheatIssueViewerPrimary({...issue, source, signal, onError}))),
        ...issues.map(issue =>
          whenAllowed(() => preheatIssueViewerSecondary({...issue, sendAnalyticsEvent, signal, onError})),
        ),
      ],
      signal,
    )
    return () => controller.abort()
  }, [issues, login, source, sendAnalyticsEvent])
}

export function issueCoordinatesFromUrl(rawHref: unknown): IssueCoordinates | undefined {
  if (typeof window === 'undefined' || typeof rawHref !== 'string') return
  try {
    const url = new URL(rawHref, window.location.href)
    if (url.origin !== window.location.origin) return
    const match = /^\/([^/]+)\/([^/]+)\/issues\/(\d+)$/.exec(url.pathname)
    if (!match?.[1] || !match[2] || !match[3]) return
    const owner = decodeURIComponent(match[1])
    const repo = decodeURIComponent(match[2])
    const number = Number(match[3])
    if (!owner || !repo || /[/\\]/.test(owner + repo) || !Number.isSafeInteger(number) || number <= 0) return
    return {owner, repo, number}
  } catch {
    // Malformed links are not preheat candidates.
    return
  }
}
